/**
 * Dashboard panel — KPIs, charts, breakdowns, forecast table.
 * Extracted from app.js without logic changes.
 */

import { els } from "../dom";
import { exportCsv, exportCanvasPng } from "../services/export";
import { getState, updateState, subscribe } from "../state";
import { formatNumber } from "../utils/formatting";
import { escapeHtml } from "../utils/escapeHtml";
import { loadPresets, savePreset, deletePreset } from "../services/filterPresets";
import { onPanelActivate, navigateTo, getActivePanel } from "../router";
import { debounce } from "../components/filters";
import { renderEmptyState } from "../components/EmptyState";
import { mountInfoTooltip } from "../components/InfoTooltip";
import { linearRegression, buildDistribution, compareValues } from "../services/analytics";
import { renderComparisonCards } from "../components/ComparisonCard";
import { renderPieChart } from "../components/PieChart";
import {
  MONTH_COLOR_THRESHOLDS,
  resolveMonthColorTier,
} from "./monthColorThresholds";
import {
  buildProjectsTable,
  mountProjectsPanel,
  showMonthDrawer,
} from "../components/ProjectsPanel";
import {
  normalizeValue,
  unique,
  fillSelect,
  inferQuarterFromDate,
  inferMonthFromDate,
  inferIsoWeekFromDate,
  ROLE_CAPABILITIES,
  roleLabel,
} from "./shared";
import type { Asset, Forecast } from "../types/index";

// ── Histogram bar geometry for tooltip hit-testing ───────────
// Updated on every renderDashboardHistogram call.
let _histBarData: Array<{
  x: number;
  right: number;
  label: string;
  total: number;
  totalPct: number;
}> = [];

// ── Init ─────────────────────────────────────────────────────

export function init(): void {
  onPanelActivate("dashboard", render);
  subscribe("assets", render);
  subscribe("forecasts", render);
  // Note: do NOT subscribe to "filtered" — render() itself updates filtered,
  // which would cause a cascading re-render loop.
  bindDashboardFilters();
  bindDashboardChartTabs();
  bindHistogramModeTabs();
  bindHistogramTooltip();
  mountKpiTooltips();
  bindForecastByTypeToggle();
  // audit C4: re-render by-type cards + histogram whenever the persisted mode changes
  subscribe("dashboardForecastMode", () => {
    renderForecastByType();
    if (getActivePanel() === "dashboard") renderDashboardHistogram();
  });
  // 330e8641: re-render histogram when count/% mode changes
  subscribe("dashboardHistMode", () => {
    if (getActivePanel() === "dashboard") renderDashboardHistogram();
  });
  syncForecastByTypeToggleVisual();

  // W3: period comparison selectors
  document.getElementById("comparison-period-a")?.addEventListener("change", renderPeriodComparison);
  document.getElementById("comparison-period-b")?.addEventListener("change", renderPeriodComparison);

  // Export CSV
  document.getElementById("export-csv-btn")?.addEventListener("click", () => {
    const state = getState();
    const data = state.filtered;
    const assetsById = Object.fromEntries(state.assets.map((a) => [a.id, a]));
    const headers = ["Product", "Brand", "Country", "Date", "Quantity", "Owner", "Notes"];
    const rows = data.map((fc) => {
      const asset = assetsById[fc.assetId];
      return [
        asset?.productName || asset?.name || fc.assetId,
        fc.brand || asset?.brand || "",
        fc.country || asset?.hub || "",
        fc.date || "",
        String(fc.quantity || 0),
        fc.owner || "",
        fc.notes || "",
      ];
    });
    const date = new Date().toISOString().slice(0, 10);
    exportCsv(headers, rows, `ecf-forecasts-${date}.csv`);
  });

  // Export histogram PNG
  document.getElementById("export-histogram-png")?.addEventListener("click", () => {
    const canvas = els.dashboardChart as HTMLCanvasElement | null;
    if (canvas) exportCanvasPng(canvas, "ecf-histogram.png");
  });

  // ── Filter Presets ──────────────────────────────────────────
  function renderPresetChips(): void {
    const host = document.getElementById("filter-presets");
    if (!host) return;
    const presets = loadPresets();
    if (!presets.length) { host.innerHTML = ""; return; }
    host.innerHTML = presets.map((p) =>
      `<button class="filter-preset-chip" data-preset-name="${escapeHtml(p.name)}" title="Load: ${escapeHtml(p.name)}">
        ${escapeHtml(p.name)}
        <span class="filter-preset-delete" data-preset-delete="${escapeHtml(p.name)}">&times;</span>
      </button>`
    ).join("");
  }

  // Save button
  document.getElementById("save-filter-preset-btn")?.addEventListener("click", () => {
    const name = prompt("Preset name:");
    if (!name?.trim()) return;
    savePreset({
      name: name.trim(),
      division: (els.filterDivision as HTMLSelectElement)?.value || "",
      brand: (els.filterBrand as HTMLSelectElement)?.value || "",
      country: (els.filterCountry as HTMLSelectElement)?.value || "",
      type: (els.filterType as HTMLSelectElement)?.value || "",
      owner: (els.filterOwner as HTMLInputElement)?.value || "",
    });
    renderPresetChips();
  });

  // Load/delete presets
  document.getElementById("filter-presets")?.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const delBtn = target.closest<HTMLElement>("[data-preset-delete]");
    if (delBtn) {
      deletePreset(delBtn.dataset.presetDelete || "");
      renderPresetChips();
      return;
    }
    const chip = target.closest<HTMLElement>("[data-preset-name]");
    if (chip) {
      const presets = loadPresets();
      const preset = presets.find((p) => p.name === chip.dataset.presetName);
      if (!preset) return;
      if (els.filterDivision) (els.filterDivision as HTMLSelectElement).value = preset.division;
      if (els.filterBrand) (els.filterBrand as HTMLSelectElement).value = preset.brand;
      if (els.filterCountry) (els.filterCountry as HTMLSelectElement).value = preset.country;
      if (els.filterType) (els.filterType as HTMLSelectElement).value = preset.type;
      if (els.filterOwner) (els.filterOwner as HTMLInputElement).value = preset.owner;
      applyDashboardFiltersAndRender();
    }
  });

  renderPresetChips();
}

// ── T1: KPI definition tooltips ──────────────────────────────
//
// Each KPI card on the dashboard surfaces a number with no inline
// explanation, leading to confusion (e.g. "Active Assets" vs "Total
// Assets"). We attach an info tooltip to the label of each card so users
// can hover or focus to read the definition. Idempotent: safe to re-call.
function mountKpiTooltips(): void {
  const labels: Array<[string, string]> = [
    ["kpi-total", "Total number of forecast line entries (one row per asset × month × country) in the filtered view."],
    ["kpi-qty", "Sum of all forecasted asset quantities for the current filter selection."],
    // N1 — make the Active vs Total distinction explicit so users stop
    // wondering why the two numbers differ. "Active" means the asset
    // already has at least one forecast entry; "Total" counts every
    // record loaded from the backend.
    ["kpi-assets", "Assets currently linked to an active project and in production scope — i.e. that already have at least one forecast entry."],
    ["kpi-countries", "Distinct countries appearing in the visible forecasts."],
    ["kpi-projects", "Distinct parent projects loaded from the assets sheet."],
    ["kpi-asset-count", "All asset records loaded from the backend, regardless of status or project linkage."],
  ];
  for (const [valueId, text] of labels) {
    const valueEl = document.getElementById(valueId);
    const card = valueEl?.closest(".kpi-card");
    const label = card?.querySelector(".kpi-label");
    if (label) mountInfoTooltip(label, text);
  }
}

// ── Render ───────────────────────────────────────────────────

export function render(): void {
  if (getActivePanel() !== "dashboard") return;
  renderFilterOptions();
  applyDashboardFiltersAndRender();
}

// ── Role ─────────────────────────────────────────────────────

export function applyRole(role: string): void {
  const normalizedRole = normalizeValue(role) as string;
  const caps = ROLE_CAPABILITIES[normalizedRole] || ROLE_CAPABILITIES.ecf;

  if (els.roleBadge) els.roleBadge.textContent = roleLabel(normalizedRole);
  if (els.currentRole) els.currentRole.textContent = roleLabel(normalizedRole);
  if (els.roleSelect) {
    (els.roleSelect as HTMLSelectElement).value = normalizedRole;
  }

  if (els.modalConfirmBtn)
    (els.modalConfirmBtn as HTMLButtonElement).disabled = !caps.canWrite;
  if (els.entryLock)
    els.entryLock.classList.toggle("hidden", caps.canWrite);

  if (normalizedRole === "ecf") {
    els.dashboardNav?.classList.remove("hidden");
    els.entryNav?.classList.add("hidden");
    els.matrixNav?.classList.remove("hidden");
    els.bookingNav?.classList.remove("hidden");
    els.bookingMatrixNav?.classList.remove("hidden");
    els.adminNav?.classList.add("hidden");
    if (
      document.getElementById("entry")?.classList.contains("is-active") ||
      document.getElementById("admin")?.classList.contains("is-active")
    ) {
      navigateTo("dashboard");
    }
    return;
  }

  if (normalizedRole === "market") {
    els.dashboardNav?.classList.remove("hidden");
    els.entryNav?.classList.remove("hidden");
    els.matrixNav?.classList.add("hidden");
    els.bookingNav?.classList.remove("hidden");
    els.bookingMatrixNav?.classList.remove("hidden");
    els.adminNav?.classList.add("hidden");
    if (document.getElementById("admin")?.classList.contains("is-active")) {
      navigateTo("dashboard");
    }
    return;
  }

  // admin: show all
  els.dashboardNav?.classList.remove("hidden");
  els.entryNav?.classList.remove("hidden");
  els.matrixNav?.classList.remove("hidden");
  els.bookingNav?.classList.remove("hidden");
  els.bookingMatrixNav?.classList.remove("hidden");
  els.adminNav?.classList.remove("hidden");
}

// ── Filters ──────────────────────────────────────────────────

function bindDashboardFilters(): void {
  const debouncedFilter = debounce(applyDashboardFiltersAndRender, 300);

  if (els.filterDivision)
    els.filterDivision.addEventListener("change", debouncedFilter);
  if (els.filterBrand)
    els.filterBrand.addEventListener("change", debouncedFilter);
  if (els.filterCountry)
    els.filterCountry.addEventListener("change", debouncedFilter);
  if (els.filterType)
    els.filterType.addEventListener("change", debouncedFilter);
  if (els.filterOwner)
    els.filterOwner.addEventListener("input", debouncedFilter);

  if (els.clearFiltersBtn) {
    els.clearFiltersBtn.addEventListener("click", () => {
      if (els.filterDivision) (els.filterDivision as HTMLSelectElement).value = "";
      if (els.filterBrand) (els.filterBrand as HTMLSelectElement).value = "";
      if (els.filterCountry) (els.filterCountry as HTMLSelectElement).value = "";
      if (els.filterType) (els.filterType as HTMLSelectElement).value = "";
      if (els.filterOwner) (els.filterOwner as HTMLSelectElement).value = "";
      applyDashboardFiltersAndRender();
    });
  }
}

function bindDashboardChartTabs(): void {
  if (!els.chartDimTabs || !els.chartDimTabs.length) return;

  for (const tab of els.chartDimTabs) {
    tab.addEventListener("click", (event: Event) => {
      const dimension = (event.currentTarget as HTMLElement).dataset.dimension;
      if (!dimension) return;

      updateState({ dashboardChartDimension: dimension });
      for (const btn of els.chartDimTabs) {
        btn.classList.toggle("is-active", (btn as HTMLElement).dataset.dimension === dimension);
      }

      renderDashboardHistogram();
      // EF-6: Update Forecast by Type when histogram dimension changes
      renderForecastByType();
    });
  }
}

function bindHistogramModeTabs(): void {
  if (!els.chartHistModeTabs || !els.chartHistModeTabs.length) return;

  for (const tab of els.chartHistModeTabs) {
    tab.addEventListener("click", (event: Event) => {
      const mode = (event.currentTarget as HTMLElement).dataset.mode as "count" | "pct" | undefined;
      if (!mode) return;

      updateState({ dashboardHistMode: mode });
      for (const btn of els.chartHistModeTabs) {
        btn.classList.toggle("is-active", (btn as HTMLElement).dataset.mode === mode);
      }
    });
  }
}

function bindHistogramTooltip(): void {
  const canvas = els.dashboardChart as HTMLCanvasElement | null;
  const tooltip = els.histTooltip as HTMLElement | null;
  if (!canvas || !tooltip) return;

  canvas.addEventListener("mousemove", (e: MouseEvent) => {
    const bar = _histBarData.find((b) => e.offsetX >= b.x && e.offsetX <= b.right);
    if (!bar) {
      tooltip.hidden = true;
      return;
    }
    tooltip.textContent = `${bar.label}: ${formatNumber(bar.total)} assets (${bar.totalPct.toFixed(1)}%)`;
    tooltip.hidden = false;
    tooltip.style.left = `${e.clientX + 14}px`;
    tooltip.style.top = `${e.clientY - 10}px`;
  });

  canvas.addEventListener("mouseleave", () => {
    tooltip.hidden = true;
  });
}

function renderFilterOptions(): void {
  const state = getState();
  const assetsById = Object.fromEntries(state.assets.map((a) => [a.id, a]));
  const divisions = unique(
    state.forecasts.map((f: any) => f.division || assetsById[f.assetId]?.division),
  );
  const brands = unique(
    state.forecasts.map((f: any) => f.brand || assetsById[f.assetId]?.brand),
  );
  const countries = unique(
    state.forecasts.map((f: any) => f.country || assetsById[f.assetId]?.hub),
  );
  const types = unique(state.assets.map((a) => a.type));

  fillSelect(els.filterDivision, divisions, "All");
  fillSelect(els.filterBrand, brands, "All");
  fillSelect(els.filterCountry, countries, "All");
  fillSelect(els.filterType, types, "All");
}

// ── Period comparison ────────────────────────────────────────

function renderPeriodComparison(): void {
  const container = document.getElementById("comparison-cards");
  const selA = document.getElementById("comparison-period-a") as HTMLSelectElement | null;
  const selB = document.getElementById("comparison-period-b") as HTMLSelectElement | null;
  if (!container || !selA || !selB) return;

  const state = getState();
  const periodA = selA.value; // e.g., "Q2"
  const periodB = selB.value; // e.g., "Q1"

  const quarterNum = (q: string) => parseInt(q.replace("Q", ""), 10);
  const inQuarter = (dateStr: string, q: number): boolean => {
    if (!dateStr) return false;
    const month = new Date(dateStr).getMonth() + 1;
    return Math.ceil(month / 3) === q;
  };

  const fcA = state.forecasts.filter((fc) => inQuarter(fc.date, quarterNum(periodA)));
  const fcB = state.forecasts.filter((fc) => inQuarter(fc.date, quarterNum(periodB)));

  const totalA = fcA.reduce((s, fc) => s + Number(fc.quantity || 0), 0);
  const totalB = fcB.reduce((s, fc) => s + Number(fc.quantity || 0), 0);
  const countA = fcA.length;
  const countB = fcB.length;
  const assetsA = new Set(fcA.map((fc) => fc.assetId)).size;
  const assetsB = new Set(fcB.map((fc) => fc.assetId)).size;

  const cmpQty = compareValues(totalA, totalB);
  const cmpCount = compareValues(countA, countB);
  const cmpAssets = compareValues(assetsA, assetsB);

  renderComparisonCards(container, [
    { label: "Total Quantity", current: totalA, previous: totalB, ...cmpQty },
    { label: "Forecast Entries", current: countA, previous: countB, ...cmpCount },
    { label: "Active Assets", current: assetsA, previous: assetsB, ...cmpAssets },
  ]);
}

// ── Core render ──────────────────────────────────────────────

export function applyDashboardFiltersAndRender(): void {
  const state = getState();
  const selectedDivision = (els.filterDivision as HTMLSelectElement)?.value || "";
  const selectedBrand = (els.filterBrand as HTMLSelectElement)?.value || "";
  const selectedCountry = (els.filterCountry as HTMLSelectElement)?.value || "";
  const selectedType = (els.filterType as HTMLSelectElement)?.value || "";
  const ownerNeedle = ((els.filterOwner as HTMLSelectElement)?.value || "").trim().toLowerCase();

  const assetsById = Object.fromEntries(state.assets.map((a) => [a.id, a]));

  const filtered = state.forecasts.filter((fc) => {
    const asset = assetsById[fc.assetId];
    const division = fc.division || asset?.division || "";
    const brand = fc.brand || asset?.brand || "";
    const country = fc.country || asset?.hub || "";
    const type = asset?.type || "";

    if (selectedDivision && division !== selectedDivision) return false;
    if (selectedBrand && brand !== selectedBrand) return false;
    if (selectedCountry && country !== selectedCountry) return false;
    if (selectedType && type !== selectedType) return false;
    if (ownerNeedle && !String(fc.owner || "").toLowerCase().includes(ownerNeedle))
      return false;
    return true;
  });

  updateState({ filtered });

  renderKpis(filtered);
  renderForecastByType(filtered);
  renderForecastTable(filtered);
  renderDashboardHistogram();
  renderBreakdowns(filtered);
  // T3: project list reflecting active filters
  const projectRows = buildProjectsTable(getState().assets, filtered);
  mountProjectsPanel(projectRows);
  bindMonthDrawerOpeners(filtered);
  // N2: filtered view indicator — visible when ≥1 filter is non-empty
  updateFilteredViewIndicator();
  // W3: distribution pie charts
  renderDistributionCharts();
  // W3: period comparison cards
  renderPeriodComparison();
}

// N2: expose a single place where we read every dashboard filter and
// produce (a) a summary string and (b) a flag for whether any is active.
// Used by the filter indicator + the Clear shortcut.
function collectActiveDashboardFilters(): { summary: string; isActive: boolean } {
  const entries: Array<[string, string]> = [];
  const division = (els.filterDivision as HTMLSelectElement | null)?.value || "";
  const brand = (els.filterBrand as HTMLSelectElement | null)?.value || "";
  const country = (els.filterCountry as HTMLSelectElement | null)?.value || "";
  const type = (els.filterType as HTMLSelectElement | null)?.value || "";
  const owner = (els.filterOwner as unknown as HTMLInputElement | null)?.value.trim() || "";
  if (division) entries.push(["Division", division]);
  if (brand) entries.push(["Brand", brand]);
  if (country) entries.push(["Country", country]);
  if (type) entries.push(["Type", type]);
  if (owner) entries.push(["Owner", owner]);
  return {
    summary: entries.map(([k, v]) => `${k}: ${v}`).join(" • "),
    isActive: entries.length > 0,
  };
}

function updateFilteredViewIndicator(): void {
  const indicator = document.getElementById("dashboard-filter-indicator");
  const summaryEl = document.getElementById("dashboard-filter-summary");
  if (!indicator || !summaryEl) return;
  const { summary, isActive } = collectActiveDashboardFilters();
  indicator.classList.toggle("hidden", !isActive);
  summaryEl.textContent = summary;
  // Bind the clear button once
  const clearBtn = document.getElementById("dashboard-filter-indicator-clear");
  if (clearBtn && !clearBtn.dataset.boundN2) {
    clearBtn.dataset.boundN2 = "1";
    clearBtn.addEventListener("click", () => {
      if (els.filterDivision) (els.filterDivision as HTMLSelectElement).value = "";
      if (els.filterBrand) (els.filterBrand as HTMLSelectElement).value = "";
      if (els.filterCountry) (els.filterCountry as HTMLSelectElement).value = "";
      if (els.filterType) (els.filterType as HTMLSelectElement).value = "";
      if (els.filterOwner) (els.filterOwner as unknown as HTMLInputElement).value = "";
      applyDashboardFiltersAndRender();
    });
  }
}

// T3: clicking a month row in the "By Month" breakdown opens a drawer with
// the projects that contributed to that month. Re-bound on every render.
function bindMonthDrawerOpeners(filtered: any[]): void {
  if (!els.monthBreakdown) return;
  els.monthBreakdown.querySelectorAll<HTMLElement>(".month-cell").forEach((cell) => {
    cell.style.cursor = "pointer";
    cell.addEventListener("click", () => {
      const label = cell.querySelector(".b-label")?.textContent || "";
      const monthFiltered = filtered.filter((row: any) => {
        const state = getState();
        const asset = state.assets.find((a) => a.id === row.assetId);
        const month =
          inferMonthFromDate(row.date) ||
          inferMonthFromDate(asset?.requestedDeliveryDate) ||
          "Unknown";
        return month === label;
      });
      const rows = buildProjectsTable(getState().assets, monthFiltered);
      showMonthDrawer(label, rows);
    });
  });
}

// ── T2 + EF-1: Forecast by Type (PDP / Zone Eventing / Local Eventing) ──
//
// Displays three project-type cards with a Brief|Asset toggle.
// - Brief mode: count of distinct *parent* projects of that category
// - Asset mode: total asset count of that category (including children)
//
// EF-1 (Marker.io #EF-1): the first iteration matched on asset.type
// with regexes like /\bpdp\b/ which never fired because the real
// asset.type values in the dataset are Launch/Reno/Hero/Inno/Refresh/…
// (product archetypes) — not marketing categories. Zone Eventing and
// Local Eventing actually live in asset.initiativeOrigin ("Zone" /
// "Local"), and PDP maps to any asset whose type contains "pdp" OR
// asset.initiativeOrigin === "DMI" (the DMI-originated product pages).
// Matchers below reflect this dual-field semantics.

type ForecastByTypeMode = "brief" | "asset";

// audit C4: mode now lives in state.ts under `dashboardForecastMode` so it
// survives panel switches and is observable via subscribe(). The two helpers
// below are the single point of access from this module.
function getForecastByTypeMode(): ForecastByTypeMode {
  return (getState().dashboardForecastMode as ForecastByTypeMode) || "brief";
}
function setForecastByTypeMode(next: ForecastByTypeMode): void {
  updateState({ dashboardForecastMode: next });
}

// EF-1: each matcher reads BOTH asset.type and asset.initiativeOrigin
// (lowercased, trimmed) so the three marketing categories are matched
// against the fields where they actually live in the dataset.
const FBT_TYPE_MATCHERS: Array<{
  key: string;
  matches: (type: string, origin: string) => boolean;
}> = [
  {
    key: "pdp",
    // PDP = any asset explicitly typed "pdp" OR originated from DMI
    // (DMI assets are the global product detail pages).
    matches: (type, origin) => /\bpdp\b/.test(type) || origin === "dmi",
  },
  {
    key: "zone-eventing",
    // Zone Eventing = any asset with initiativeOrigin === "Zone".
    // Optionally also match if the type contains "zone" + "event".
    matches: (type, origin) => origin === "zone" || /zone.*event/.test(type),
  },
  {
    key: "local-eventing",
    // Local Eventing = any asset with initiativeOrigin === "Local".
    matches: (type, origin) => origin === "local" || /local.*event/.test(type),
  },
];

export function computeForecastByType(
  assets: Asset[],
  mode: ForecastByTypeMode,
): Record<string, number> {
  const counts: Record<string, number> = {
    pdp: 0,
    "zone-eventing": 0,
    "local-eventing": 0,
  };
  const seenProjects: Record<string, Set<string>> = {
    pdp: new Set(),
    "zone-eventing": new Set(),
    "local-eventing": new Set(),
  };

  for (const asset of assets) {
    const type = String(asset.type || "").toLowerCase().trim();
    const origin = String(asset.initiativeOrigin || "")
      .toLowerCase()
      .trim();
    // Accept assets with either a type OR an origin. Previously we
    // continue'd when type was empty, which skipped perfectly valid
    // rows that only had initiativeOrigin set.
    if (!type && !origin) continue;
    for (const matcher of FBT_TYPE_MATCHERS) {
      if (!matcher.matches(type, origin)) continue;
      if (mode === "asset") {
        counts[matcher.key] += 1;
      } else {
        const isParent = normalizeValue(asset.parentChild) === "parent";
        const project = String(asset.projectName || "").trim();
        if (isParent && project && !seenProjects[matcher.key].has(project)) {
          seenProjects[matcher.key].add(project);
          counts[matcher.key] += 1;
        } else if (!project && isParent) {
          counts[matcher.key] += 1;
        }
      }
      break; // a given asset only matches one category
    }
  }
  return counts;
}

function renderForecastByType(filtered?: Forecast[]): void {
  const state = getState();
  // Restrict to assets that appear in the currently filtered forecast view,
  // so the cards react to the dashboard filters.
  const data = filtered || state.filtered;
  const visibleAssetIds = new Set(data.map((row) => row.assetId));
  const visibleAssets = state.assets.filter((a) => visibleAssetIds.has(a.id));
  const counts = computeForecastByType(visibleAssets, getForecastByTypeMode());

  const set = (id: string, value: number): void => {
    const el = document.getElementById(id);
    if (el) el.textContent = formatNumber(value);
  };
  set("fbt-pdp", counts.pdp);
  set("fbt-zone-eventing", counts["zone-eventing"]);
  set("fbt-local-eventing", counts["local-eventing"]);

  // EF-1: make an all-zero state explicit instead of silently showing
  // three zero cards (which made the toggle look broken). Also toggle
  // a class on the section so CSS can dim it slightly when empty.
  const section = document.getElementById("forecast-by-type");
  const totalForCategory = counts.pdp + counts["zone-eventing"] + counts["local-eventing"];
  const isEmpty = totalForCategory === 0 && visibleAssets.length > 0;
  section?.classList.toggle("fbt-empty", isEmpty);
  let emptyHint = section?.querySelector<HTMLElement>(".fbt-empty-hint");
  if (isEmpty) {
    if (!emptyHint && section) {
      emptyHint = document.createElement("p");
      emptyHint.className = "fbt-empty-hint";
      section.appendChild(emptyHint);
    }
    if (emptyHint) {
      emptyHint.textContent =
        "No projects tagged PDP, Zone Eventing or Local Eventing in the current filter. " +
        "Forecast by Type reads asset.type and asset.initiativeOrigin — make sure your " +
        "assets carry one of these categories.";
    }
  } else if (emptyHint) {
    emptyHint.remove();
  }

  // Sync the toggle button visuals to the persisted mode (handles the case
  // where the state was changed externally or restored from a re-mount).
  syncForecastByTypeToggleVisual();
}

function syncForecastByTypeToggleVisual(): void {
  const mode = getForecastByTypeMode();
  document
    .querySelectorAll<HTMLButtonElement>(".fbt-toggle-btn")
    .forEach((btn) => {
      const isActive = btn.dataset.fbtMode === mode;
      btn.classList.toggle("is-active", isActive);
      btn.setAttribute("aria-selected", String(isActive));
    });
}

function bindForecastByTypeToggle(): void {
  const buttons = document.querySelectorAll<HTMLButtonElement>(".fbt-toggle-btn");
  for (const btn of buttons) {
    btn.addEventListener("click", () => {
      const next = (btn.dataset.fbtMode || "brief") as ForecastByTypeMode;
      if (next === getForecastByTypeMode()) return;
      setForecastByTypeMode(next);
      // The subscribe() in init() will trigger renderForecastByType which
      // also handles the visual sync, but we update immediately for snappiness.
      syncForecastByTypeToggleVisual();
    });
  }
}

// ── KPIs ─────────────────────────────────────────────────────

/**
 * N1 — Canonical definition of an "active" asset on the Dashboard.
 *
 * An asset is considered ACTIVE when it appears in at least one forecast
 * entry within the currently filtered dataset. That is: the asset is
 * currently linked to an active project (something, somewhere, has
 * already scheduled a quantity for it) as opposed to sitting idle in
 * the assets sheet without any demand attached.
 *
 * This is why ACTIVE ASSETS (distinct assetIds in the filtered forecast
 * set) is usually much smaller than TOTAL ASSETS (every asset record
 * loaded from the backend, regardless of project linkage). The gap is
 * not a bug — it's the size of the catalog waiting for forecasts.
 */
export function countActiveAssets(filteredForecasts: Array<{ assetId?: string }>): number {
  return new Set(
    filteredForecasts
      .map((row) => row.assetId)
      .filter((id): id is string => Boolean(id)),
  ).size;
}

function renderKpis(filtered?: Forecast[]): void {
  const state = getState();
  const data = filtered || state.filtered;
  const total = data.length;
  const qty = data.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
  const activeAssets = countActiveAssets(data);

  const assetsById = Object.fromEntries(state.assets.map((a) => [a.id, a]));
  const countries = new Set(
    data
      .map((row) => assetsById[row.assetId]?.hub || row.country)
      .filter(Boolean),
  ).size;

  const projectNames = new Set(
    state.assets
      .filter((a) => normalizeValue(a.parentChild) === "parent")
      .map((a) => String(a.projectName || "").trim())
      .filter(Boolean),
  );

  if (els.kpiTotal) els.kpiTotal.textContent = String(total);
  if (els.kpiQty) els.kpiQty.textContent = formatNumber(qty);
  if (els.kpiAssets) els.kpiAssets.textContent = String(activeAssets);
  if (els.kpiCountries) els.kpiCountries.textContent = String(countries);
  if (els.kpiProjects) els.kpiProjects.textContent = String(projectNames.size);
  if (els.kpiAssetCount) els.kpiAssetCount.textContent = String(state.assets.length);
}

// ── Forecast table ───────────────────────────────────────────

function renderForecastTable(filtered?: Forecast[]): void {
  const state = getState();
  const data = filtered || state.filtered;
  const assetsById = Object.fromEntries(state.assets.map((a) => [a.id, a]));

  const rows = data.slice(0, 20).map((fc: any) => {
    const asset = assetsById[fc.assetId];
    const brand = fc.brand || asset?.brand || "";
    const country = fc.country || asset?.hub || "";
    return `<tr>
      <td>${escapeHtml(asset?.productName || asset?.name || fc.assetId)}</td>
      <td>${escapeHtml(brand)}</td>
      <td>${escapeHtml(country)}</td>
      <td>${escapeHtml(fc.date || "")}</td>
      <td>${escapeHtml(formatNumber(fc.quantity || 0))}</td>
      <td>${escapeHtml(fc.owner || "")}</td>
      <td>${escapeHtml(fc.notes || "")}</td>
    </tr>`;
  });

  if (els.tableBody) {
    if (!rows.length) {
      renderEmptyState(els.tableBody, {
        icon: "📊",
        message: "No matching forecasts",
        hint: "Try adjusting your filters above.",
      });
    } else {
      els.tableBody.innerHTML = rows.join("");
    }
  }
}

// ── Breakdowns ───────────────────────────────────────────────

function renderBreakdowns(filtered?: Forecast[]): void {
  const divisionData = aggregateBy("division", filtered);
  const quarterData = aggregateByQuarter(filtered);
  const monthData = aggregateByMonth(filtered);
  const monthMeta = aggregateMonthAssetCounts(filtered);

  if (els.divisionBreakdown)
    els.divisionBreakdown.innerHTML = renderBreakdownHtml(divisionData, "#2563eb");
  if (els.quarterBreakdown)
    els.quarterBreakdown.innerHTML = renderBreakdownHtml(quarterData, "#7c3aed");
  if (els.monthBreakdown) {
    // T4: Launch Calendar cells now expose two labelled values per month —
    // the large number is the "Forecast quantity" (sum of row.quantity) and
    // the small number is the count of distinct assets that contribute to it.
    // Tooltip surfaces both values plus the project count for the month.
    els.monthBreakdown.innerHTML = renderMonthBreakdownHtml(monthData, monthMeta, "#10b981");
  }
  mountMonthBreakdownInfo();
  mountMonthColorLegend();
  updateMonthBreakdownSubtitle();
}

// T5: keep the subtitle under "By Month" in sync with the dashboard Type
// filter so users immediately know whether the calendar is showing every
// project type or has been narrowed down. Reads the live <select> value.
function updateMonthBreakdownSubtitle(): void {
  const subtitle = document.getElementById("month-breakdown-subtitle");
  if (!subtitle) return;
  const selectedType = (els.filterType as HTMLSelectElement | null)?.value || "";
  if (!selectedType) {
    subtitle.textContent =
      "Showing all project types (Launch, Eventing, Reno, PDP, Local).";
  } else {
    subtitle.textContent = `Showing only "${selectedType}" project type.`;
  }
}

function aggregateBy(field: string, filtered?: Forecast[]): [string, number][] {
  const state = getState();
  const data = filtered || state.filtered;
  const assetsById = Object.fromEntries(state.assets.map((a) => [a.id, a]));
  const map = new Map<string, number>();

  for (const row of data) {
    const asset = assetsById[row.assetId];
    const rec = asset as Record<string, unknown> | undefined;
    const key =
      String(rec?.[field] || (row as Record<string, unknown>)[field] || "") ||
      (field === "country" ? row?.country : "") ||
      "Unknown";
    const next = (map.get(key) || 0) + Number(row.quantity || 0);
    map.set(key, next);
  }

  return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8);
}

function aggregateByQuarter(filtered?: Forecast[]): [string, number][] {
  const state = getState();
  const data = filtered || state.filtered;
  const assetsById = Object.fromEntries(state.assets.map((a) => [a.id, a]));
  const map = new Map<string, number>();

  for (const row of data) {
    const asset = assetsById[row.assetId];
    const key =
      asset?.quarter || inferQuarterFromDate(row.date) || "Unknown";
    const next = (map.get(key) || 0) + Number(row.quantity || 0);
    map.set(key, next);
  }

  return [...map.entries()].sort((a, b) => b[1] - a[1]);
}

// aggregateByMonth — sums row.quantity per month (YYYY-MM key, "Unknown" bucket
// for rows without a resolvable date). The returned value is the LARGE number
// shown in each Launch Calendar cell ("Forecast quantity" / "Quantity").
// Includes ALL forecast types (Launch, Eventing, Reno, PDP, Local, …) from the
// currently filtered dataset.
function aggregateByMonth(filtered?: Forecast[]): [string, number][] {
  const state = getState();
  const data = filtered || state.filtered;
  const assetsById = Object.fromEntries(state.assets.map((a) => [a.id, a]));
  const map = new Map<string, number>();

  for (const row of data) {
    const asset = assetsById[row.assetId];
    const key =
      inferMonthFromDate(row.date) ||
      inferMonthFromDate(asset?.requestedDeliveryDate) ||
      "Unknown";
    const next = (map.get(key) || 0) + Number(row.quantity || 0);
    map.set(key, next);
  }

  return [...map.entries()].sort((a, b) => {
    if (a[0] === "Unknown") return 1;
    if (b[0] === "Unknown") return -1;
    return a[0].localeCompare(b[0]);
  });
}

// T4: companion of aggregateByMonth — for each month, also computes the
// number of distinct contributing assets and projects, so the Launch Calendar
// cell can label both values clearly.
export function aggregateMonthAssetCounts(
  filtered?: Forecast[],
): Map<string, { assets: number; projects: number }> {
  const state = getState();
  const data = filtered || state.filtered;
  const assetsById = Object.fromEntries(state.assets.map((a) => [a.id, a]));
  const buckets = new Map<string, { assets: Set<string>; projects: Set<string> }>();

  for (const row of data) {
    const asset = assetsById[row.assetId];
    const key =
      inferMonthFromDate(row.date) ||
      inferMonthFromDate(asset?.requestedDeliveryDate) ||
      "Unknown";
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { assets: new Set(), projects: new Set() };
      buckets.set(key, bucket);
    }
    if (row.assetId) bucket.assets.add(String(row.assetId));
    const project =
      asset?.projectName || "";
    if (project) bucket.projects.add(String(project));
  }

  const out = new Map<string, { assets: number; projects: number }>();
  for (const [k, v] of buckets) {
    out.set(k, { assets: v.assets.size, projects: v.projects.size });
  }
  return out;
}

// T4 + N4: dedicated renderer for the "By Month" breakdown. Each cell
// is colored by the N4 threshold tier (green→blue→orange→red) so users
// spot rush months at a glance. The tooltip reveals the tier name too.
function renderMonthBreakdownHtml(
  items: [string, number][],
  meta: Map<string, { assets: number; projects: number }>,
  _color: string,
): string {
  if (!items.length) {
    return '<div class="breakdown-item"><span class="b-label">No data</span></div>';
  }
  const max = Math.max(...items.map(([, value]) => value), 1);
  return items
    .map(([label, value]) => {
      const pct = Math.round((value / max) * 100);
      const monthMeta = meta.get(label) || { assets: 0, projects: 0 };
      const tier = resolveMonthColorTier(value);
      const tip =
        `${label} | Load: ${tier.label} | Forecast quantity: ${formatNumber(value)} ` +
        `| Distinct assets: ${formatNumber(monthMeta.assets)} ` +
        `| Projects: ${formatNumber(monthMeta.projects)}`;
      return `<div class="breakdown-item month-cell" title="${escapeHtml(tip)}">
        <span class="b-label">${escapeHtml(label)}</span>
        <div class="b-bar"><div class="b-bar-fill" style="width:${pct}%; background:${tier.color}"></div></div>
        <span class="b-value">
          <span class="b-value-main">${escapeHtml(formatNumber(value))}</span>
          <span class="b-value-sub">${escapeHtml(formatNumber(monthMeta.assets))} assets</span>
        </span>
      </div>`;
    })
    .join("");
}

// N4: inject a color legend above the By Month breakdown explaining
// what each color means. Idempotent — safe to call on every render.
function mountMonthColorLegend(): void {
  if (!els.monthBreakdown) return;
  const card = els.monthBreakdown.parentElement;
  if (!card) return;
  let legend = card.querySelector<HTMLElement>(".month-color-legend");
  if (!legend) {
    legend = document.createElement("div");
    legend.className = "month-color-legend";
    // Insert BEFORE the breakdown list so the legend is above the bars.
    card.insertBefore(legend, els.monthBreakdown);
  }
  legend.innerHTML = MONTH_COLOR_THRESHOLDS.map((tier, i) => {
    const prev = i === 0 ? 0 : MONTH_COLOR_THRESHOLDS[i - 1].upperBound;
    const range =
      tier.upperBound === Infinity
        ? `> ${formatNumber(prev)}`
        : `${formatNumber(prev)} – ${formatNumber(tier.upperBound)}`;
    return `<span class="month-color-chip">
      <span class="month-color-dot" style="background:${tier.color}"></span>
      <span class="month-color-label">${escapeHtml(tier.label)}</span>
      <span class="month-color-range">(${escapeHtml(range)})</span>
    </span>`;
  }).join("");
}

// T4: surface a definition tooltip on the "By Month" card title so users
// understand what the two values per cell represent. Idempotent.
function mountMonthBreakdownInfo(): void {
  const heading = els.monthBreakdown?.parentElement?.querySelector("h3");
  if (heading) {
    mountInfoTooltip(
      heading,
      "Each month shows the total Forecast quantity (large) and the number " +
        "of distinct assets contributing to it (small). Includes every forecast " +
        "type (Launch, Eventing, Reno, PDP, Local) from the currently filtered data.",
    );
  }
}

function renderBreakdownHtml(items: [string, number][], color: string): string {
  if (!items.length) {
    return '<div class="breakdown-item"><span class="b-label">No data</span></div>';
  }

  const max = Math.max(...items.map(([, value]) => value), 1);

  return items
    .map(([label, value]) => {
      const pct = Math.round((value / max) * 100);
      return `<div class="breakdown-item">
        <span class="b-label">${escapeHtml(label)}</span>
        <div class="b-bar"><div class="b-bar-fill" style="width:${pct}%; background:${color}"></div></div>
        <span class="b-value">${escapeHtml(formatNumber(value))}</span>
      </div>`;
    })
    .join("");
}

// ── Histogram ────────────────────────────────────────────────

/**
 * "Asset" mode: sum forecast quantities per dimension × type.
 * Used when dashboardForecastMode === "asset" (default).
 */
function aggregateByDimensionAndType(
  labelResolver: (asset: any, row: any) => string,
): { types: string[]; rows: { label: string; total: number; byType: Map<string, number> }[] } {
  const state = getState();
  const assetsById = Object.fromEntries(state.assets.map((a) => [a.id, a]));
  const groups = new Map<string, { label: string; total: number; byType: Map<string, number> }>();
  const allTypes = new Set<string>();

  for (const row of state.filtered) {
    const asset = assetsById[row.assetId];
    const label = labelResolver(asset, row) || "Unknown";
    const type = asset?.type || row.type || "Unknown";
    const qty = Number(row.quantity || 0);

    if (!groups.has(label)) {
      groups.set(label, { label, total: 0, byType: new Map() });
    }

    const group = groups.get(label)!;
    group.total += qty;
    group.byType.set(type, (group.byType.get(type) || 0) + qty);
    allTypes.add(type);
  }

  return {
    types: [...allTypes].sort((a, b) => a.localeCompare(b)),
    rows: [...groups.values()].sort((a, b) => b.total - a.total).slice(0, 8),
  };
}

/**
 * "Project" mode: count distinct projects per dimension × type.
 * Used when dashboardForecastMode === "brief".
 */
function aggregateProjectsByDimension(
  labelResolver: (asset: any) => string,
): { types: string[]; rows: { label: string; total: number; byType: Map<string, number> }[] } {
  const state = getState();
  // Restrict to parent assets whose IDs appear in the filtered forecast view.
  const visibleAssetIds = new Set(state.filtered.map((r) => r.assetId));
  const allTypes = new Set<string>();
  // label → { total, byType, seenKeys: Set<"projectName::type"> }
  const groups = new Map<
    string,
    { label: string; total: number; byType: Map<string, number>; seen: Set<string> }
  >();

  for (const asset of state.assets) {
    if (normalizeValue(asset.parentChild) !== "parent") continue;
    if (!visibleAssetIds.has(asset.id)) continue;
    const project = String(asset.projectName || "").trim();
    if (!project) continue;
    const label = labelResolver(asset) || "Unknown";
    const type = asset.type || "Unknown";
    const dedupeKey = `${project}\x00${type}`;

    if (!groups.has(label)) {
      groups.set(label, { label, total: 0, byType: new Map(), seen: new Set() });
    }
    const group = groups.get(label)!;
    if (!group.seen.has(dedupeKey)) {
      group.seen.add(dedupeKey);
      group.total += 1;
      group.byType.set(type, (group.byType.get(type) || 0) + 1);
      allTypes.add(type);
    }
  }

  return {
    types: [...allTypes].sort((a, b) => a.localeCompare(b)),
    rows: [...groups.values()]
      .map(({ seen: _seen, ...rest }) => rest)
      .sort((a, b) => b.total - a.total)
      .slice(0, 8),
  };
}

function resolveDashboardDimensionLabel(
  asset: any,
  row: any,
  dimension: string,
): string {
  if (dimension === "brand") return asset?.brand || row.brand || "Unknown";
  if (dimension === "country") return asset?.hub || row.country || "Unknown";
  if (dimension === "division") return asset?.division || row.division || "Unknown";
  if (dimension === "quarter")
    return asset?.quarter || inferQuarterFromDate(row.date) || "Unknown";
  if (dimension === "month")
    return (
      inferMonthFromDate(row.date) ||
      inferMonthFromDate(asset?.requestedDeliveryDate) ||
      "Unknown"
    );
  // N3 — ISO week bucket. Fallback on asset.requestedDeliveryDate or
  // asset.ocdDate so rows without a forecast.date still land in a week.
  if (dimension === "week")
    return (
      inferIsoWeekFromDate(row.date) ||
      inferIsoWeekFromDate(asset?.requestedDeliveryDate) ||
      inferIsoWeekFromDate(asset?.ocdDate) ||
      "Unknown"
    );
  return "Unknown";
}

function renderDashboardHistogram(): void {
  const canvas = els.dashboardChart as HTMLCanvasElement | null;
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const state = getState();
  const dimension = state.dashboardChartDimension || "brand";
  const mode = getForecastByTypeMode();
  const isProjectMode = mode === "brief";

  const dataset = isProjectMode
    ? aggregateProjectsByDimension((asset) =>
        resolveDashboardDimensionLabel(asset, {}, dimension),
      )
    : aggregateByDimensionAndType((asset, row) =>
        resolveDashboardDimensionLabel(asset, row, dimension),
      );

  // Update the histogram title to reflect the active mode
  const histogramTitle = document.getElementById("histogram-title");
  if (histogramTitle) {
    histogramTitle.textContent = isProjectMode
      ? "Quantity Histogram — Projects"
      : "Quantity Histogram — Assets";
  }
  const rows = dataset.rows;
  const types = dataset.types;
  const histMode = state.dashboardHistMode || "count";
  // N3: Week view can produce many bars. Expand the canvas beyond the
  // container width and let CSS overflow-x handle the scroll so each bar
  // keeps a readable width. Other dimensions stay at 100% of the wrap.
  const baseWidth = canvas.parentElement?.clientWidth || canvas.clientWidth || 980;
  const barThreshold = 20;
  const widthPerBar = 48;
  const needsWideWidth = rows.length > barThreshold;
  const width = needsWideWidth
    ? Math.max(baseWidth, rows.length * widthPerBar + 60)
    : baseWidth;
  const height = 300;
  canvas.width = width;
  canvas.height = height;
  canvas.style.width = `${width}px`;
  ctx.clearRect(0, 0, width, height);

  if (!rows.length) {
    ctx.fillStyle = "#94a3b8";
    ctx.font = "14px IBM Plex Sans";
    ctx.fillText("No data", 16, 24);
    _histBarData = [];
    return;
  }

  const palette = [
    "#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
    "#06b6d4", "#84cc16", "#f97316", "#ec4899",
  ];
  const typeColor = new Map(
    types.map((type, index) => [type, palette[index % palette.length]]),
  );

  // EF-2: dimLabel was displayed as a debug label "View: BRAND" in the
  // top-left of the canvas, which looked like a stray technical string.
  // The active dimension is already highlighted in the chart-tabs above,
  // so the duplicate is removed.
  const legendCols = 3;
  const legendRows = Math.max(1, Math.ceil(types.length / legendCols));
  const legendX = 12;
  const legendY = 20;
  const legendColWidth = Math.max(130, Math.floor((width - 24) / legendCols));

  types.forEach((type, index) => {
    const col = index % legendCols;
    const row = Math.floor(index / legendCols);
    const x = legendX + col * legendColWidth;
    const y = legendY + row * 16;

    ctx.fillStyle = typeColor.get(type)!;
    ctx.fillRect(x, y - 8, 10, 10);
    ctx.fillStyle = "#cbd5e1";
    ctx.font = "11px IBM Plex Sans";
    ctx.fillText(type.length > 18 ? `${type.slice(0, 18)}...` : type, x + 14, y);
  });

  const left = 42;
  const right = width - 16;
  const top = legendY + legendRows * 16 + 18;
  const bottom = height - 46;
  const innerWidth = right - left;
  const innerHeight = bottom - top;
  const maxTotal = Math.max(...rows.map((entry) => entry.total), 1);
  const grandTotal = rows.reduce((sum, entry) => sum + entry.total, 0) || 1;
  const barGap = 10;
  const barWidth = Math.max(
    16,
    (innerWidth - barGap * (rows.length - 1)) / rows.length,
  );

  // EF-2: the "View: <dim>" debug label is removed — the chart-tabs
  // toggle already shows which dimension is active.
  ctx.strokeStyle = "rgba(148,163,184,0.24)";
  ctx.beginPath();
  ctx.moveTo(left, bottom);
  ctx.lineTo(right, bottom);
  ctx.stroke();

  // 330e8641: y-axis gridlines + labels in % mode
  if (histMode === "pct") {
    const gridTicks = [25, 50, 75, 100];
    ctx.font = "10px IBM Plex Sans";
    ctx.fillStyle = "#64748b";
    ctx.textAlign = "end";
    for (const pct of gridTicks) {
      const gridY = bottom - (pct / 100) * innerHeight;
      ctx.strokeStyle = "rgba(148,163,184,0.15)";
      ctx.beginPath();
      ctx.moveTo(left, gridY);
      ctx.lineTo(right, gridY);
      ctx.stroke();
      ctx.fillText(`${pct}%`, left - 4, gridY + 4);
    }
    ctx.textAlign = "start";
  }

  // 330e8641: populate bar geometry for hover tooltip
  _histBarData = [];

  rows.forEach((entry, index) => {
    const x = left + index * (barWidth + barGap);
    let yCursor = bottom;

    types.forEach((type) => {
      const value = entry.byType.get(type) || 0;
      if (!value) return;
      // In % mode each segment's height is its share of the grand total so bars
      // are visually proportional across columns (tallest bar = highest share).
      const segmentHeight =
        histMode === "pct"
          ? (value / grandTotal) * innerHeight
          : (value / maxTotal) * innerHeight;
      yCursor -= segmentHeight;

      ctx.fillStyle = typeColor.get(type)!;
      ctx.fillRect(x, yCursor, barWidth, Math.max(1, segmentHeight));
    });

    // 330e8641: record bar for tooltip hit-testing
    _histBarData.push({
      x,
      right: x + barWidth,
      label: entry.label,
      total: entry.total,
      totalPct: (entry.total / grandTotal) * 100,
    });

    ctx.fillStyle = "#94a3b8";
    ctx.font = "11px IBM Plex Sans";
    ctx.textAlign = "center";
    // 330e8641: top label shows count in count mode, % of grand total in % mode
    const topLabel =
      histMode === "pct"
        ? `${Math.round((entry.total / grandTotal) * 100)}%`
        : formatNumber(entry.total);
    ctx.fillText(topLabel, x + barWidth / 2, yCursor - 6);

    const short =
      entry.label.length > 10 ? `${entry.label.slice(0, 10)}...` : entry.label;
    ctx.fillStyle = "#cbd5e1";
    ctx.fillText(short, x + barWidth / 2, bottom + 14);
  });

  ctx.textAlign = "start";

  // W3: Trend line overlay
  if (rows.length >= 3) {
    const totals = rows.map((r) => r.total);
    const reg = linearRegression(totals);
    const scale = histMode === "pct" ? 100 : maxTotal;

    ctx.strokeStyle = "rgba(250, 204, 21, 0.7)";
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();

    for (let i = 0; i < rows.length; i++) {
      const x = left + i * (barWidth + barGap) + barWidth / 2;
      const yVal = reg.slope * i + reg.intercept;
      const y = bottom - (yVal / scale) * innerHeight;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.lineWidth = 1;
  }
}

// ── W3: Distribution pie charts ─────────────────────────────

function renderDistributionCharts(): void {
  const state = getState();
  const data = state.filtered;
  const assetsById = Object.fromEntries(state.assets.map((a: Asset) => [a.id, a]));

  const PALETTE = [
    "#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
    "#06b6d4", "#84cc16", "#f97316", "#ec4899", "#14b8a6",
  ];

  // Brand distribution
  const brandCanvas = document.getElementById("pie-brand") as HTMLCanvasElement | null;
  if (brandCanvas) {
    const brandItems = data.map((fc: Forecast) => ({
      category: fc.brand || assetsById[fc.assetId]?.brand || "Unknown",
      value: Number(fc.quantity || 0),
    }));
    const brandDist = buildDistribution(brandItems);
    renderPieChart(brandCanvas, brandDist.slice(0, 8).map((d, i) => ({
      label: d.label,
      value: d.value,
      color: PALETTE[i % PALETTE.length],
    })), { title: "By Brand", donut: true });
  }

  // Division distribution
  const divCanvas = document.getElementById("pie-division") as HTMLCanvasElement | null;
  if (divCanvas) {
    const divItems = data.map((fc: Forecast) => ({
      category: assetsById[fc.assetId]?.division || "Unknown",
      value: Number(fc.quantity || 0),
    }));
    const divDist = buildDistribution(divItems);
    renderPieChart(divCanvas, divDist.slice(0, 8).map((d, i) => ({
      label: d.label,
      value: d.value,
      color: PALETTE[i % PALETTE.length],
    })), { title: "By Division", donut: true });
  }
}

// ── 330e8641: pure helper — exported for unit tests ───────────

/**
 * Given histogram rows, returns each row enriched with its percentage of the
 * grand total. Used by renderDashboardHistogram in % mode and tested directly.
 */
export function enrichHistogramRowsWithPct(
  rows: Array<{ label: string; total: number; byType: Map<string, number> }>,
): Array<{ label: string; total: number; totalPct: number; byType: Map<string, number> }> {
  const grandTotal = rows.reduce((sum, r) => sum + r.total, 0);
  return rows.map((r) => ({
    ...r,
    totalPct: grandTotal > 0 ? (r.total / grandTotal) * 100 : 0,
  }));
}
